import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import {
  CNY_PER_USD_MAX,
  CNY_PER_USD_MIN,
  type GetPricingResponse,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { usePricing, useUpsertPricing } from '../api/pricing.ts';
import { Modal } from '../components/Modal.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS, INPUT_CLASS_MONO_ON_HIGHEST } from '../styles/forms.ts';

/**
 * `/pricing` — exchange-rate policy admin page.
 *
 * DESIGN.md §5+§6 card layout — three stacked `surface-container` cards:
 *   1. Current policy — `R` (cny_per_usd), updated-by, updated-at.
 *   2. Market reference — latest Frankfurter / exchangerate.host sample
 *      + spread % vs the current policy.
 *   3. Edit form — new R + optional note → type-to-confirm modal →
 *      POST /v1/ops/pricing.
 *
 * Safety on the edit form:
 *   - The input accepts decimals (inputMode="decimal"). Form-level
 *     validation rejects non-positive / out-of-range values before
 *     opening the modal so the operator sees the error inline.
 *   - Submit opens a confirm modal that shows before → after R + the
 *     spread-vs-market + the note. The modal requires the operator to
 *     TYPE the new R (including its decimal point) exactly — same
 *     type-to-confirm pattern as gift-card redeem-on-behalf and
 *     balance-adjust, so muscle memory carries across surfaces.
 *   - After save: toast + full refetch (policy + market).
 *
 * No debouncing / auto-save: ops exchange-rate changes are rare and
 * irreversible (L1 margin gets booked at the new rate); deliberate
 * type-to-confirm is the point.
 */

// Client-side bounds come from `@nadoutong/contracts` (CNY_PER_USD_MIN /
// CNY_PER_USD_MAX) so the server + UI can't drift. This form validates
// eagerly for UX, but platform-api is the authority — see
// `apps/platform-api/src/routes/internal-pricing.ts`.

/** Format a number as USD with symbol. Used for labels like "¥X = $1". */
function formatCny(rate: number): string {
  return `¥${rate.toFixed(4)}`;
}

function formatIso(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  return new Date(parsed).toISOString().replace('T', ' ').slice(0, 19) + 'Z';
}

/**
 * Spread between the current policy and the latest market reference,
 * expressed as a percentage. Positive → operator is charging above
 * market (typical — L1 margin); negative → below market (unusual).
 *
 * Returns `null` when either input is missing / non-finite; the
 * consumer renders em-dash in that case.
 */
function computeSpread(policyRate: number | null, marketRate: number | null): number | null {
  if (policyRate === null || marketRate === null) return null;
  if (!Number.isFinite(policyRate) || !Number.isFinite(marketRate) || marketRate <= 0) {
    return null;
  }
  return ((policyRate - marketRate) / marketRate) * 100;
}

function formatSpread(spread: number | null): string {
  if (spread === null) return '—';
  const sign = spread > 0 ? '+' : '';
  return `${sign}${spread.toFixed(2)}%`;
}

export function PricingPage(): JSX.Element {
  const t = useT();
  const pricing = usePricing();

  if (pricing.isPending) {
    return (
      <section className="flex flex-col gap-8" data-testid="pricing-page-loading">
        <div className="h-32 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <div className="h-32 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <div className="h-40 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <span className="sr-only">{t('pricing.loading_aria')}</span>
      </section>
    );
  }

  if (pricing.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-xl bg-error-container p-6 text-on-error-container"
        role="alert"
        data-testid="pricing-page-error"
      >
        <h2 className="text-base font-semibold">{t('pricing.error.title')}</h2>
        <p className="text-sm">
          {pricing.error instanceof OpsApiError
            ? `${pricing.error.code}: ${pricing.error.message}`
            : t('common.network_error')}
        </p>
        <div>
          <button
            type="button"
            onClick={() => void pricing.refetch()}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.retry')}
          </button>
        </div>
      </section>
    );
  }

  return <PricingReady data={pricing.data} />;
}

interface ReadyProps {
  readonly data: GetPricingResponse;
}

function PricingReady({ data }: ReadyProps): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-8" data-testid="pricing-page">
      <header className="flex flex-col gap-3">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('pricing.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('pricing.subtitle')}</p>
      </header>
      <PolicyCard data={data} />
      <MarketCard data={data} />
      <EditCard data={data} />
    </section>
  );
}

// ─── Policy card ─────────────────────────────────────────────────────────

interface PolicyCardProps {
  readonly data: GetPricingResponse;
}

function PolicyCard({ data }: PolicyCardProps): JSX.Element {
  const t = useT();
  const policy = data.policy;
  return (
    <article
      data-testid="pricing-policy-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('pricing.policy.title')}
        </h3>
      </header>
      {policy === null ? (
        <p className="text-sm text-outline" data-testid="pricing-policy-empty">
          {t('pricing.policy.empty')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field
              label={t('pricing.policy.pair')}
              value={policy.pair.replace('_', ' → ')}
              mono
            />
            <Field
              label={t('pricing.policy.current_r')}
              value={formatCny(policy.cny_per_usd)}
              mono
              testId="pricing-policy-rate"
            />
            <Field
              label={t('pricing.policy.updated_by')}
              value={policy.updated_by}
              mono
              testId="pricing-policy-updated-by"
            />
            <Field
              label={t('pricing.policy.updated_at')}
              value={formatIso(policy.updated_at)}
              mono
              testId="pricing-policy-updated-at"
            />
          </div>
          <div
            className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-6"
            data-testid="pricing-policy-summary"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
              {t('pricing.policy.meaning.title')}
            </span>
            <span className="text-sm text-on-surface">
              {t('pricing.policy.meaning.body', { rate: policy.cny_per_usd.toFixed(4) })}
            </span>
            {policy.note !== null && policy.note.length > 0 ? (
              <span
                className="mt-2 border-t border-outline-variant pt-2 text-xs text-on-surface-variant"
                data-testid="pricing-policy-note"
              >
                {t('pricing.policy.note_prefix')} {policy.note}
              </span>
            ) : null}
          </div>
        </>
      )}
    </article>
  );
}

// ─── Market reference card ───────────────────────────────────────────────

interface MarketCardProps {
  readonly data: GetPricingResponse;
}

function MarketCard({ data }: MarketCardProps): JSX.Element {
  const t = useT();
  const market = data.latest_market;
  const policyRate = data.policy?.cny_per_usd ?? null;
  const marketRate = market?.rate ?? null;
  const spread = computeSpread(policyRate, marketRate);

  return (
    <article
      data-testid="pricing-market-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('pricing.market.title')}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
          {t('pricing.market.subtitle')}
        </span>
      </header>
      {market === null ? (
        <p className="text-sm text-outline" data-testid="pricing-market-empty">
          {t('pricing.market.empty')}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field
            label={t('pricing.market.latest_rate')}
            value={formatCny(market.rate)}
            mono
            testId="pricing-market-rate"
          />
          <Field
            label={t('pricing.market.source')}
            value={market.source}
            mono
            testId="pricing-market-source"
          />
          <Field
            label={t('pricing.market.sampled_at')}
            value={formatIso(market.sampled_at)}
            mono
            testId="pricing-market-sampled-at"
          />
          <Field
            label={t('pricing.market.spread')}
            value={formatSpread(spread)}
            mono
            valueClass={spread !== null && spread < 0 ? 'text-error' : 'text-primary'}
            testId="pricing-market-spread"
          />
        </div>
      )}
    </article>
  );
}

// ─── Edit card + confirmation modal ──────────────────────────────────────

interface EditCardProps {
  readonly data: GetPricingResponse;
}

interface DraftPricing {
  readonly rawRate: string;
  readonly rate: number;
  readonly note: string;
}

function EditCard({ data }: EditCardProps): JSX.Element {
  const t = useT();
  // Seed the input with the current rate (4 decimal places to match the
  // column's REAL precision) so the operator edits from a known baseline
  // rather than an empty string. The default value only applies on first
  // render — re-editing during the same session preserves the input.
  const defaultRate =
    data.policy?.cny_per_usd !== undefined ? data.policy.cny_per_usd.toFixed(4) : '';
  const [rawRate, setRawRate] = useState(defaultRate);
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftPricing | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const trimmed = rawRate.trim();
    if (trimmed.length === 0) {
      setFormError(t('pricing.edit.error.required'));
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      setFormError(t('pricing.edit.error.not_number'));
      return;
    }
    if (parsed <= CNY_PER_USD_MIN || parsed > CNY_PER_USD_MAX) {
      setFormError(
        t('pricing.edit.error.out_of_range', {
          min: String(CNY_PER_USD_MIN),
          max: String(CNY_PER_USD_MAX),
        }),
      );
      return;
    }
    setDraft({ rawRate: trimmed, rate: parsed, note: note.trim() });
  }

  function handleConfirmClose(success: boolean): void {
    setDraft(null);
    if (success) {
      // Leave the form populated with the new rate so the operator
      // can see what they just saved at a glance; they can clear +
      // reset manually if they want.
      setRawRate(draft?.rawRate ?? rawRate);
      setNote('');
    }
  }

  return (
    <article
      data-testid="pricing-edit-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('pricing.edit.title')}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
          {t('pricing.edit.subtitle')}
        </span>
      </header>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('pricing.edit.new_r')}
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={rawRate}
              onChange={(e) => setRawRate(e.currentTarget.value)}
              placeholder={t('pricing.edit.rate_placeholder')}
              data-testid="pricing-edit-rate"
              className={INPUT_CLASS}
              autoComplete="off"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('pricing.edit.note')}
            </span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              placeholder={t('pricing.edit.note_placeholder')}
              data-testid="pricing-edit-note"
              className={INPUT_CLASS}
              autoComplete="off"
            />
          </label>
        </div>
        {formError !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="pricing-edit-error"
          >
            {formError}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setRawRate(defaultRate);
              setNote('');
              setFormError(null);
            }}
            data-testid="pricing-edit-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            data-testid="pricing-edit-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('pricing.edit.submit')}
          </button>
        </div>
      </form>

      {draft !== null ? (
        <EditConfirmModal
          data={data}
          draft={draft}
          onClose={(success) => handleConfirmClose(success)}
        />
      ) : null}
    </article>
  );
}

interface EditConfirmModalProps {
  readonly data: GetPricingResponse;
  readonly draft: DraftPricing;
  readonly onClose: (success: boolean) => void;
}

function EditConfirmModal({ data, draft, onClose }: EditConfirmModalProps): JSX.Element {
  const t = useT();
  const upsert = useUpsertPricing();
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const previousRate = data.policy?.cny_per_usd ?? null;
  const marketRate = data.latest_market?.rate ?? null;
  const projectedSpread = useMemo(
    () => computeSpread(draft.rate, marketRate),
    [draft.rate, marketRate],
  );
  const typedMatches = typed.trim() === draft.rawRate;

  useEffect(() => {
    setServerError(null);
  }, [typed]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setServerError(null);
    if (!typedMatches) {
      setServerError(t('pricing.confirm.error.type_mismatch', { rate: draft.rawRate }));
      return;
    }
    try {
      await upsert.mutateAsync({
        cny_per_usd: draft.rate,
        // Only include the note field when the operator wrote one —
        // an empty string persists as empty (matches platform-api
        // behaviour), while `undefined` leaves the column NULL-ish.
        // We pass explicit empty-string so clearing a previous note
        // round-trips the way the operator expects.
        note: draft.note,
      });
      toast.success(t('pricing.toast.updated', { rate: formatCny(draft.rate) }));
      onClose(true);
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setServerError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!upsert.isPending) onClose(false);
      }}
      titleId="pricing-confirm-title"
      testId="pricing-edit-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="pricing-confirm-title" className="text-base font-semibold text-on-surface">
          {t('pricing.confirm.title')}
        </h2>
        <div className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-4 text-sm text-outline">
          <Row
            label={t('pricing.confirm.previous')}
            value={previousRate !== null ? formatCny(previousRate) : '—'}
            mono
            testId="pricing-modal-previous"
          />
          <Row
            label={t('pricing.confirm.new')}
            value={formatCny(draft.rate)}
            mono
            valueClass="text-primary"
            testId="pricing-modal-next"
          />
          <Row
            label={t('pricing.confirm.market')}
            value={marketRate !== null ? formatCny(marketRate) : '—'}
            mono
            testId="pricing-modal-market"
          />
          <Row
            label={t('pricing.confirm.projected_spread')}
            value={formatSpread(projectedSpread)}
            mono
            valueClass={
              projectedSpread !== null && projectedSpread < 0 ? 'text-error' : 'text-primary'
            }
            testId="pricing-modal-spread"
          />
          {draft.note.length > 0 ? (
            <Row
              label={t('pricing.confirm.note')}
              value={draft.note}
              testId="pricing-modal-note"
            />
          ) : null}
        </div>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('pricing.confirm.type_hint', { rate: draft.rawRate })}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            placeholder={draft.rawRate}
            data-testid="pricing-modal-typed"
            disabled={upsert.isPending}
            className={INPUT_CLASS_MONO_ON_HIGHEST}
            autoComplete="off"
          />
        </label>
        {serverError !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="pricing-modal-error"
          >
            {serverError}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              if (!upsert.isPending) onClose(false);
            }}
            disabled={upsert.isPending}
            data-testid="pricing-modal-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={upsert.isPending || !typedMatches}
            data-testid="pricing-modal-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {upsert.isPending
              ? t('pricing.confirm.submit.saving')
              : t('pricing.confirm.submit.save', { rate: formatCny(draft.rate) })}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Shared row / field layout helpers ───────────────────────────────────

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly valueClass?: string;
  readonly testId?: string;
}

function Field({ label, value, mono, valueClass, testId }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <span
        className={`break-all text-sm ${mono === true ? 'font-mono' : ''} ${
          valueClass ?? 'text-on-surface'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

interface RowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly valueClass?: string;
  readonly testId?: string;
}

function Row({ label, value, mono, valueClass, testId }: RowProps): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${mono === true ? 'font-mono' : ''} ${
          valueClass ?? 'text-on-surface'
        }`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}
